/**
 * Native app bootstrap (Capacitor).
 *
 * Everything here is a no-op on the web build — guarded by
 * `Capacitor.isNativePlatform()` — so the browser/PWA experience is untouched.
 * Call `initNativeApp()` once from main.tsx after storage hydration.
 */

import { Capacitor } from '@capacitor/core';

export const isNativePlatform = (): boolean => Capacitor.isNativePlatform();
export const nativePlatform = (): 'ios' | 'android' | 'web' =>
  Capacitor.getPlatform() as 'ios' | 'android' | 'web';

/** Light haptic tap for primary navigation/actions. No-op on web. */
export async function hapticTap(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle.Light });
  } catch {
    /* haptics unavailable — ignore */
  }
}

export async function initNativeApp(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;

  // Tag the document so CSS can target the native shell (safe-area padding,
  // hide web-only chrome, native scroll behaviour, etc.).
  document.documentElement.classList.add('capacitor-native', `platform-${Capacitor.getPlatform()}`);

  // Status bar: dark icons on our light UI; opaque (no overlay) so content
  // never renders under the clock/battery row.
  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Light });
    if (Capacitor.getPlatform() === 'android') {
      await StatusBar.setOverlaysWebView({ overlay: false });
      await StatusBar.setBackgroundColor({ color: '#ffffff' });
    }
  } catch {
    /* status-bar unavailable — ignore */
  }

  // Hardware back button (Android): go back in the SPA history, or minimise
  // the app at the root instead of killing it (Play UX expectation).
  try {
    const { App } = await import('@capacitor/app');
    App.addListener('backButton', ({ canGoBack }) => {
      if (canGoBack || window.history.length > 1) {
        window.history.back();
      } else {
        void App.minimizeApp();
      }
    });
  } catch {
    /* app plugin unavailable — ignore */
  }

  // Keyboard: toggle a class so layouts can collapse fixed bottom-nav while typing.
  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.addListener('keyboardWillShow', () =>
      document.documentElement.classList.add('keyboard-open'),
    );
    Keyboard.addListener('keyboardWillHide', () =>
      document.documentElement.classList.remove('keyboard-open'),
    );
  } catch {
    /* keyboard plugin unavailable — ignore */
  }

  // Hide the splash once the web layer is interactive.
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch {
    /* splash plugin unavailable — ignore */
  }
}
